import { Box, DesignSystemProvider, Flex } from '@hover/blueprint';
import React, { useReducer } from 'react';
import { Route, Switch, useHistory } from 'react-router';

import { CobrandedHeader } from 'src/modules/homeownerApp/components/CobrandedHeader/CobrandedHeader';
import { setUserSession } from 'src/modules/homeownerApp/hooks';
import { PartnerAffiliateOrgIdProvider } from 'src/modules/homeownerApp/hooks/usePartner';
import { CheckEmail } from 'src/modules/onboarding/components/CheckEmail';
import { CreateAccountPro } from 'src/modules/onboarding/components/CreateAccountPro';
import { Login } from 'src/modules/onboarding/components/Login';
import { MobileOnboardingHeader } from 'src/modules/onboarding/components/MobileOnboardingHeader';
import {
  OnboardingContext,
  onboardingFormReducer,
} from 'src/modules/onboarding/components/OnboardingContext';
import { Password } from 'src/modules/onboarding/components/Password';
import { ResetPassword } from 'src/modules/onboarding/components/ResetPassword';
import { SSOFailure } from 'src/modules/onboarding/components/SSOFailure';
import { SSOSuccess } from 'src/modules/onboarding/components/SSOSuccess';
import { SignupHomeowner } from 'src/modules/onboarding/components/SignupHomeowner';
import { SignupPro } from 'src/modules/onboarding/components/SignupPro';
import { Start } from 'src/modules/onboarding/components/Start';
import { UserOrgDescription } from 'src/modules/onboarding/components/UserOrgDescription';
import { Welcome } from 'src/modules/onboarding/components/Welcome';
import { ExternalRedirect } from 'src/shared/components';
import { Title as MetaTitle } from 'src/shared/components/Meta/Title/index';
import { StyledNavBar } from 'src/shared/components/NavBar';
import { SetIntercomVisibility } from 'src/shared/components/SetIntercomVisibility';
import { useDetectUserSessionQuery } from 'src/shared/graphql/generated';
import { useEffectOnMount, useLocation } from 'src/shared/hooks';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { BrandProvider } from 'src/shared/providers/Brand';
import { Route as Routes } from 'src/shared/route';
import { pageViewed } from 'src/shared/telemetry';
import { baseUrlToRedirectUrl } from 'src/shared/utils/url';

import { UserOrgUsage } from './UserOrgUsage';

export const Router = () => {
  const reducerTuple = useReducer(onboardingFormReducer, {});

  const history = useHistory();

  const { href, search } = useLocation();
  const queryParams = new URLSearchParams(search);

  const publicIdentifier = queryParams.get('aff_org_id');
  const sessionId = queryParams.get('session_id') || undefined;

  useEffectOnMount(() => {
    if (sessionId) {
      setUserSession(sessionId);
      pageViewed({
        page_or_screen_name: Routes.SIGN_UP_ONBOARDING.page,
        session_id: sessionId,
      });
    }
  });

  const { data } = useDetectUserSessionQuery({
    fetchPolicy: 'network-only',
  });

  const { homeownerAppUploadFile } = useGlobalStorageContext();

  // not redirect user in the signup flow and sso
  const excludedPaths = [
    '/ssoSuccess',
    '/description',
    '/usage',
    '/personal-info',
  ];

  if (data && !excludedPaths.some(path => href.includes(path))) {
    // if we got a file waiting to be uploaded, redirect to the
    // homeownerApp without losing the context
    if (homeownerAppUploadFile) {
      history.push(Routes.HOMEOWNER_APP_HOME.path);
      return null;
    }

    return (
      <ExternalRedirect
        url={baseUrlToRedirectUrl(href, `${Routes.PROPERTIES.path}${search}`)}
      />
    );
  }

  return (
    <Switch>
      <Route path={Routes.SIGN_UP_ONBOARDING.path}>
        <DesignSystemProvider attach>
          <Flex flexDir="column" height="100dvh">
            <MetaTitle overwrite title="Hover" />
            <SetIntercomVisibility visible={false} />
            <BrandProvider>
              {!publicIdentifier && (
                <>
                  {/* // I think this should ideally be in the shared component, but as it is now only the */}
                  {/* // onboarding navbar has a separating line. */}
                  <Box
                    borderBottomColor="neutral.200"
                    borderBottomStyle="solid"
                    borderBottomWidth="400"
                    display={{ base: 'none', tablet: 'flex' }}
                    marginBottom={900}
                    width={1}
                  >
                    <StyledNavBar
                      redirectPath={Routes.ROOT.path}
                      useDefaultLogo
                    />
                  </Box>
                  <MobileOnboardingHeader />
                </>
              )}
              {publicIdentifier && (
                <PartnerAffiliateOrgIdProvider affOrgId={publicIdentifier}>
                  <CobrandedHeader />
                </PartnerAffiliateOrgIdProvider>
              )}
              <OnboardingContext.Provider value={reducerTuple}>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/start`}>
                  <Start />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/homeowner`}
                >
                  <PartnerAffiliateOrgIdProvider
                    affOrgId={publicIdentifier}
                    skip={!publicIdentifier}
                  >
                    <SignupHomeowner />
                  </PartnerAffiliateOrgIdProvider>
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/pro`}>
                  <SignupPro />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/insurance`}
                >
                  <SignupPro />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/personal-info`}
                >
                  <CreateAccountPro />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/welcome`}>
                  <Welcome />
                </Route>
                <Route
                  exact
                  path={[
                    `${Routes.SIGN_UP_ONBOARDING.path}/login`,
                    `${Routes.SIGN_UP_ONBOARDING.path}/login/sso`,
                  ]}
                >
                  <Login />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/ssoSuccess`}
                >
                  <SSOSuccess />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/ssoFailure`}
                >
                  <SSOFailure />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/check`}>
                  <CheckEmail />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/password`}
                >
                  <Password />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/reset`}>
                  <ResetPassword />
                </Route>
                <Route
                  exact
                  path={`${Routes.SIGN_UP_ONBOARDING.path}/description`}
                >
                  <UserOrgDescription />
                </Route>
                <Route exact path={`${Routes.SIGN_UP_ONBOARDING.path}/usage`}>
                  <UserOrgUsage />
                </Route>
              </OnboardingContext.Provider>
            </BrandProvider>
          </Flex>
        </DesignSystemProvider>
      </Route>
    </Switch>
  );
};
